import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface StatsModalProps {
  onClose: () => void;
}

interface MonthlyStats {
  month: string;
  earned: number;
  spent: number;
}

export default function StatsModal({ onClose }: StatsModalProps) {
  const { user } = useAuth();
  const [stats, setStats] = useState<MonthlyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'6months' | '1year'>('6months');

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    if (!user) return;

    setLoading(true);

    try {
      const monthsAgo = period === '6months' ? 6 : 12;
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - monthsAgo);

      const { data, error } = await supabase
        .from('point_transactions')
        .select('amount, created_at')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // 월별로 그룹화
      const monthlyData: { [key: string]: { earned: number; spent: number } } = {};

      data?.forEach((transaction) => {
        const date = new Date(transaction.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { earned: 0, spent: 0 };
        }

        if (transaction.amount > 0) {
          monthlyData[monthKey].earned += transaction.amount;
        } else {
          monthlyData[monthKey].spent += Math.abs(transaction.amount);
        }
      });

      // 배열로 변환
      const statsArray = Object.entries(monthlyData).map(([month, data]) => ({
        month,
        earned: data.earned,
        spent: data.spent,
      }));

      setStats(statsArray);
    } catch (error) {
      console.error('통계 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const maxValue = Math.max(
    ...stats.map(s => Math.max(s.earned, s.spent)),
    1
  );

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    return `${year}년 ${parseInt(month)}월`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">달란트 통계</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setPeriod('6months')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              period === '6months'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            최근 6개월
          </button>
          <button
            onClick={() => setPeriod('1year')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              period === '1year'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            최근 1년
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
          </div>
        ) : stats.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-bar-chart-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">통계 데이터가 없습니다</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* 차트 */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="space-y-4">
                {stats.map((stat) => (
                  <div key={stat.month}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {formatMonth(stat.month)}
                      </span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600">
                          +{stat.earned.toLocaleString()}
                        </span>
                        <span className="text-red-600">
                          -{stat.spent.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 h-8">
                      <div
                        className="bg-green-500 rounded transition-all"
                        style={{ width: `${(stat.earned / maxValue) * 100}%` }}
                      ></div>
                      <div
                        className="bg-red-500 rounded transition-all"
                        style={{ width: `${(stat.spent / maxValue) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 범례 */}
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm text-gray-600">적립</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm text-gray-600">사용</span>
              </div>
            </div>

            {/* 요약 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-sm text-green-700 mb-1">총 적립</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.reduce((sum, s) => sum + s.earned, 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-4">
                <p className="text-sm text-red-700 mb-1">총 사용</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.reduce((sum, s) => sum + s.spent, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
